import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/UI'
import { materialCalculatorApi } from '../../services/materialCalculator'
import MCWizardLayout, { WIZARD_STEPS } from './components/MCWizardLayout'
import MCRepeaterGrid from './components/MCRepeaterGrid'
import MCCalcPreviewPanel from './components/MCCalcPreviewPanel'
import MCBoQTable from './components/MCBoQTable'

const initialForms = {
  foundation: { areaM2: 0, depthM: 0.5, thicknessM: 0.2, fillFactor: 0 },
  structure: { slabs: [], beams: [], columns: [] },
  masonry: { walls: [] },
  tiles: { floorAreaM2: 0, wallAreaM2: 0, tileType: 'ceramica', wasteFactor: 0.1 },
  paint: { wallAreaM2: 0, roofAreaM2: 0, coats: 2, coverageM2PerLiter: 10 },
  finishes: { items: [] },
}

export default function MCWizardStepPage() {
  const navigate = useNavigate()
  const { projectId, step = 'foundation' } = useParams()
  const [forms, setForms] = useState(initialForms)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)
  const [statuses, setStatuses] = useState({})

  useEffect(() => {
    materialCalculatorApi.listInputs(projectId).then((rows) => {
      const next = { ...initialForms }
      rows.forEach((row) => {
        if (next[row.sectionKey] !== undefined) {
          try { next[row.sectionKey] = JSON.parse(row.inputJson || '{}') } catch { }
        }
      })
      setForms(next)
    }).catch(() => undefined)
  }, [projectId])

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        if (forms[step] !== undefined) {
          await materialCalculatorApi.saveInput(projectId, step, JSON.stringify(forms[step]))
        }
      } catch {
        // ignore autosave errors in UI for now
      }
    }, 450)

    return () => clearTimeout(t)
  }, [forms, step, projectId])

  useEffect(() => {
    const next = {}
    for (const s of Object.keys(initialForms)) {
      const value = forms[s]
      if (!value) next[s] = 'incomplete'
      else if (JSON.stringify(value) === JSON.stringify(initialForms[s])) next[s] = 'incomplete'
      else next[s] = 'complete'
    }
    setStatuses(next)
  }, [forms])

  const update = (key, value) => setForms((prev) => ({ ...prev, [step]: { ...prev[step], [key]: value } }))

  const calculate = async () => {
    setSaving(true)
    try {
      const foundation = forms.foundation
      const structure = forms.structure
      const masonry = forms.masonry
      const tiles = forms.tiles
      const paint = forms.paint

      const beams = (structure.beams || []).reduce((acc, b) => acc + (Number(b.count || 0) * Number(b.widthM || 0) * Number(b.depthM || 0) * Number(b.lengthM || 0)), 0)
      const columns = (structure.columns || []).reduce((acc, c) => acc + (Number(c.count || 0) * Number(c.widthM || 0) * Number(c.depthM || 0) * Number(c.heightM || 0)), 0)
      const slabs = (structure.slabs || []).reduce((acc, s) => acc + (Number(s.lengthM || 0) * Number(s.widthM || 0) * Number(s.thicknessM || 0)), 0)
      const wallArea = (masonry.walls || []).reduce((acc, w) => acc + Math.max((Number(w.lengthM || 0) * Number(w.heightM || 0)) - Number(w.openingsAreaM2 || 0), 0), 0)

      const payload = {
        areaM2: Number(foundation.areaM2 || 0),
        depthM: Number(foundation.depthM || 0),
        thicknessM: Number(foundation.thicknessM || 0),
        lengthM: slabs > 0 ? slabs : 1,
        widthM: 1,
        heightM: 1,
        count: 1,
        openingsAreaM2: 0,
        unitsPerM2: 12.5,
        mortarM3PerM2: 0.02,
        tileAreaM2: Number(tiles.floorAreaM2 || 0) + Number(tiles.wallAreaM2 || 0),
        wasteFactor: Number(tiles.wasteFactor || 0),
        paintAreaM2: Number(paint.wallAreaM2 || 0) + Number(paint.roofAreaM2 || 0),
        coats: Number(paint.coats || 0),
        coverageM2PerLiter: Number(paint.coverageM2PerLiter || 10),
        stuccoKgPerM2: 4.5,
      }

      const response = await materialCalculatorApi.runTakeoff(projectId, payload)
      setResult({ ...response, beams, columns, slabs, wallArea })
      setStatuses((prev) => ({ ...prev, [step]: 'complete' }))
    } finally {
      setSaving(false)
    }
  }

  const previewValues = useMemo(() => {
    if (!result) return []
    return Object.entries(result).slice(0, 8).map(([label, value]) => ({ label, value: Number.isFinite(value) ? Number(value).toFixed(2) : value }))
  }, [result])

  const currentIndex = WIZARD_STEPS.findIndex((s) => s.key === step)
  const goPrev = () => currentIndex > 0 && navigate(`/material-calculator/projects/${projectId}/wizard/${WIZARD_STEPS[currentIndex - 1].key}`)
  const goNext = () => currentIndex < WIZARD_STEPS.length - 1 && navigate(`/material-calculator/projects/${projectId}/wizard/${WIZARD_STEPS[currentIndex + 1].key}`)

  return (
    <MCWizardLayout projectId={projectId} currentStep={step} statuses={statuses} onPrevious={goPrev} onNext={goNext} onCalculate={calculate} saving={saving}>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle>Paso del asistente / {step}</CardTitle><CardDescription>Guardado automático por sección habilitado.</CardDescription></CardHeader>
            <CardContent>
              {step === 'foundation' && (
                <div className="grid grid-cols-2 gap-2">
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.foundation.areaM2} onChange={(e) => update('areaM2', Number(e.target.value))} placeholder="Área m²" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.foundation.depthM} onChange={(e) => update('depthM', Number(e.target.value))} placeholder="Profundidad m" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.foundation.thicknessM} onChange={(e) => update('thicknessM', Number(e.target.value))} placeholder="Espesor m" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.foundation.fillFactor} onChange={(e) => update('fillFactor', Number(e.target.value))} placeholder="Factor relleno %" />
                </div>
              )}

              {step === 'structure' && (
                <div className="space-y-3">
                  <MCRepeaterGrid title="Losas" rows={forms.structure.slabs} onChange={(rows) => update('slabs', rows)} columns={[{ key: 'lengthM', label: 'Largo m', type: 'number', span: 3 }, { key: 'widthM', label: 'Ancho m', type: 'number', span: 3 }, { key: 'thicknessM', label: 'Espesor m', type: 'number', span: 3 }]} />
                  <MCRepeaterGrid title="Vigas" rows={forms.structure.beams} onChange={(rows) => update('beams', rows)} columns={[{ key: 'count', label: 'Cant', type: 'number', span: 2 }, { key: 'widthM', label: 'Ancho', type: 'number', span: 3 }, { key: 'depthM', label: 'Alto', type: 'number', span: 3 }, { key: 'lengthM', label: 'Largo', type: 'number', span: 3 }]} />
                  <MCRepeaterGrid title="Columnas" rows={forms.structure.columns} onChange={(rows) => update('columns', rows)} columns={[{ key: 'count', label: 'Cant', type: 'number', span: 2 }, { key: 'widthM', label: 'Ancho', type: 'number', span: 3 }, { key: 'depthM', label: 'Fondo', type: 'number', span: 3 }, { key: 'heightM', label: 'Altura', type: 'number', span: 3 }]} />
                </div>
              )}

              {step === 'masonry' && (
                <MCRepeaterGrid title="Muros" rows={forms.masonry.walls} onChange={(rows) => update('walls', rows)} columns={[{ key: 'name', label: 'Nombre', span: 3 }, { key: 'lengthM', label: 'Long m', type: 'number', span: 2 }, { key: 'heightM', label: 'Alt m', type: 'number', span: 2 }, { key: 'openingsAreaM2', label: 'Vanos m²', type: 'number', span: 2 }, { key: 'formatKey', label: 'Formato', span: 2 }]} />
              )}

              {step === 'tiles' && (
                <div className="grid grid-cols-2 gap-2">
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.tiles.floorAreaM2} onChange={(e) => update('floorAreaM2', Number(e.target.value))} placeholder="Piso m²" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.tiles.wallAreaM2} onChange={(e) => update('wallAreaM2', Number(e.target.value))} placeholder="Pared m²" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.tiles.wasteFactor} onChange={(e) => update('wasteFactor', Number(e.target.value))} placeholder="Merma" />
                  <select className="rounded border px-3 py-2" value={forms.tiles.tileType} onChange={(e) => update('tileType', e.target.value)}><option value="ceramica">Cerámica</option><option value="porcelanato">Porcelanato</option><option value="otro">Otro</option></select>
                </div>
              )}

              {step === 'paint' && (
                <div className="grid grid-cols-2 gap-2">
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.paint.wallAreaM2} onChange={(e) => update('wallAreaM2', Number(e.target.value))} placeholder="Área muros m²" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.paint.roofAreaM2} onChange={(e) => update('roofAreaM2', Number(e.target.value))} placeholder="Área techos m²" />
                  <input className="rounded border px-3 py-2" type="number" value={forms.paint.coats} onChange={(e) => update('coats', Number(e.target.value))} placeholder="Manos" />
                  <input className="rounded border px-3 py-2" type="number" step="any" value={forms.paint.coverageM2PerLiter} onChange={(e) => update('coverageM2PerLiter', Number(e.target.value))} placeholder="Rendimiento m²/l" />
                </div>
              )}

              {step === 'finishes' && (
                <MCRepeaterGrid title="Acabados" rows={forms.finishes.items} onChange={(rows) => update('items', rows)} columns={[{ key: 'name', label: 'Nombre', span: 3 }, { key: 'unit', label: 'Unidad', span: 2 }, { key: 'baseQty', label: 'Cantidad', type: 'number', span: 2 }, { key: 'consumption', label: 'Consumo/u', type: 'number', span: 2 }, { key: 'waste', label: 'Merma %', type: 'number', span: 2 }]} />
              )}

              {step === 'results' && <MCBoQTable items={Object.entries(result || {}).map(([k, v]) => ({ chapter: 'General', material: k, unit: '-', quantity: Number(v).toFixed ? Number(v).toFixed(2) : v }))} />}
            </CardContent>
          </Card>
        </div>

        <MCCalcPreviewPanel values={previewValues} />
      </div>
    </MCWizardLayout>
  )
}
